/**
 * Vincent #138/#139/#140 (Eric 2026-05-16) — Modal d'import de connaissances
 * depuis un fichier (CSV/JSON/MD/.docx/.xlsx).
 *
 * Permet à un admin de uploader un fichier de questions/procédures/scripts/
 * objections/concepts et de créer en masse des knowledge_units en statut
 * pending_review. Elles apparaissent ensuite dans la file de validation
 * rapide et la base de connaissances comme n'importe quel item IA.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadSimple, X, CheckCircle, Warning, SpinnerGap, Info } from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";

const KIND_OPTIONS = [
  { id: "qa",        label_fr: "Q&R",          label_en: "Q&A",         hint_fr: "question, réponse[, tags]", hint_en: "question, answer[, tags]" },
  { id: "procedure", label_fr: "Procédures",   label_en: "Procédures",  hint_fr: "title, summary[, steps]",   hint_en: "title, summary[, steps]" },
  { id: "script",    label_fr: "Scripts",      label_en: "Scripts",     hint_fr: "title, text",               hint_en: "title, text" },
  { id: "objection", label_fr: "Objections",   label_en: "Objections",  hint_fr: "objection, response",       hint_en: "objection, response" },
  { id: "concept",   label_fr: "Concepts",     label_en: "Concepts",    hint_fr: "term, definition",          hint_en: "term, definition" },
];

const ACCEPTED_FORMATS = ".csv,.json,.md,.markdown,.docx,.xlsx";

export default function ImportKnowledgeModal({ open, onClose, onImported, videoId = null, videoTitle = null }) {
  const { lang } = useLang();
  const [kind, setKind] = useState("qa");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const selectedKind = KIND_OPTIONS.find(k => k.id === kind);

  const submit = async () => {
    if (!file) {
      toast.error(lang === "fr" ? "Veuillez choisir un fichier" : "Please select a file");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      // Eric (2026-05-18) — Si on importe depuis l'onglet d'une vidéo, on lie
      // les Q&R/scripts/etc. créés à cette vidéo (champ video_id côté backend).
      // Elles apparaîtront alors dans la liste des Q&R de cette vidéo comme si
      // elles avaient été générées par le pipeline IA (status: pending_review).
      if (videoId) form.append("video_id", videoId);
      const { data } = await axios.post(`${API}/api/imports/knowledge`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.inserted > 0) {
        toast.success(`${data.inserted} ${lang === "fr" ? "élément(s) importé(s)" : "item(s) imported"}`);
        onImported?.(data);
      } else {
        toast.warning(lang === "fr" ? "Aucun élément importé" : "No items imported");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : (lang === "fr" ? "Erreur import" : "Import error"));
    } finally {
      setUploading(false);
    }
  };

  const reset = () => { setFile(null); setResult(null); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="import-knowledge-modal">
      <div className="absolute inset-0 bg-black/50" onClick={uploading ? undefined : onClose} />
      <div className="relative bg-card rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <UploadSimple size={20} weight="duotone" className="text-[#002FA7]" />
            {lang === "fr" ? "Importer des connaissances" : "Import knowledge"}
          </h2>
          <button
            onClick={onClose}
            disabled={uploading}
            className="p-1 hover:bg-secondary rounded disabled:opacity-50"
            data-testid="import-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Eric (2026-05-18) — Bandeau "import contextuel" : quand le modal
              est ouvert depuis la page d'une vidéo, on précise clairement à
              l'utilisateur que toutes les Q&R seront rattachées à cette vidéo. */}
          {videoId && (
            <div
              className="bg-[#002FA7]/5 border border-[#002FA7]/30 rounded-lg px-3 py-2 text-xs text-[#002FA7] flex items-start gap-2"
              data-testid="import-video-context"
            >
              <Info size={14} weight="duotone" className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold">
                  {lang === "fr" ? "Import lié à cette vidéo" : "Import linked to this video"}
                </p>
                <p className="text-[10.5px] text-zinc-600 mt-0.5">
                  {lang === "fr"
                    ? <>Les éléments seront rattachés à <strong className="truncate inline-block max-w-[260px] align-bottom">{videoTitle || videoId}</strong> et apparaîtront dans les onglets Q&R / Scripts / Procédures / Connaissances de cette vidéo, comme s'ils avaient été générés par l'IA.</>
                    : <>Items will be attached to <strong>{videoTitle || videoId}</strong> and appear in this video's Q&A / Scripts / Procédures / Knowledge tabs.</>}
                </p>
              </div>
            </div>
          )}

          {/* Type selector */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-1.5">
              {lang === "fr" ? "Type de connaissance" : "Knowledge type"}
            </label>
            <div className="flex flex-wrap gap-2">
              {KIND_OPTIONS.map(k => (
                <button
                  key={k.id}
                  onClick={() => setKind(k.id)}
                  data-testid={`import-kind-${k.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                    kind === k.id
                      ? "bg-[#002FA7] text-white font-semibold"
                      : "bg-secondary text-muted-foreground hover:bg-secondary/70"
                  }`}
                >
                  {lang === "fr" ? k.label_fr : k.label_en}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 inline-flex items-center gap-1">
              <Info size={11} /> {lang === "fr" ? "Colonnes attendues" : "Expected columns"} : <code className="px-1 bg-secondary rounded">{lang === "fr" ? selectedKind.hint_fr : selectedKind.hint_en}</code>
            </p>
            {/* Vincent #156 (Eric 2026-05-27 round 5) — Confirmé explicitement
                que d'autres formats sont acceptés (15 cols Vincent, 4 cols
                reformatté, ordres variables…) pour dissiper le "il impose
                toujours son format". */}
            {selectedKind?.id === "qa" && (
              <div className="mt-2 px-3 py-2 rounded bg-teal-50 border border-teal-200 text-[10.5px] text-teal-900" data-testid="import-format-vincent-hint">
                <strong>✓ {lang === "fr" ? "Formats Vincent supportés" : "Vincent's formats supported"} :</strong>{" "}
                {lang === "fr"
                  ? "le format 15 colonnes (catégorie · dossier · vidéo · date · thèmes · mots clefs · question · réponse · temps début · type · citation complète · ... · nom du cours · URL Dropbox) ET le format reformatté 4 colonnes (question · réponse · tags · preuve textuelle). Les en-têtes longs sont reconnus automatiquement, et le timestamp est extrait de la preuve s'il n'est pas dans une colonne séparée."
                  : "the 15-col format AND the reformatted 4-col format. Long headers auto-detected, timestamps extracted from proof column if needed."}
              </div>
            )}
          </div>

          {/* File picker */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-1.5">
              {lang === "fr" ? "Fichier" : "File"} <span className="text-rose-500">*</span>
            </label>
            <input
              type="file"
              accept={ACCEPTED_FORMATS}
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              disabled={uploading}
              className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#002FA7] file:text-white file:font-semibold hover:file:bg-[#002280] file:cursor-pointer disabled:opacity-50"
              data-testid="import-file-input"
            />
            <p className="text-[10px] text-muted-foreground mt-1.5">
              {lang === "fr"
                ? "Formats acceptés : CSV, JSON, Markdown (.md), Word (.docx), Excel (.xlsx) — max 5 MB"
                : "Accepted: CSV, JSON, Markdown (.md), Word (.docx), Excel (.xlsx) — 5 MB max"}
            </p>
          </div>

          {/* Result panel */}
          {result && (
            <div
              className={`border rounded-lg p-3 text-xs ${
                result.inserted > 0
                  ? "bg-teal-50 border-teal-200 text-teal-900"
                  : "bg-amber-50 border-amber-200 text-amber-900"
              }`}
              data-testid="import-result-panel"
            >
              <div className="font-semibold mb-1.5 inline-flex items-center gap-1">
                {result.inserted > 0 ? <CheckCircle size={13} weight="fill" /> : <Warning size={13} weight="fill" />}
                {lang === "fr"
                  ? `${result.inserted} importé(s), ${result.skipped} ignoré(s)`
                  : `${result.inserted} imported, ${result.skipped} skipped`}
                {" · "}<code className="text-[10px] opacity-70">{result.kind}</code>
              </div>
              {result.errors?.length > 0 && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[10px] underline">
                    {lang === "fr" ? `Voir les ${result.errors.length} erreur(s)` : `View ${result.errors.length} error(s)`}
                  </summary>
                  <ul className="mt-1 ml-3 list-disc text-[10px] space-y-0.5">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
              {result.items_preview?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-current/20">
                  <div className="text-[10px] font-semibold mb-0.5">{lang === "fr" ? "Aperçu :" : "Preview:"}</div>
                  <ul className="text-[10px] space-y-0.5">
                    {result.items_preview.map((p) => <li key={p.id}>• {p.title}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            {result?.inserted > 0 ? (
              <>
                <button onClick={reset} className="px-3 py-1.5 text-xs rounded border border-border hover:bg-secondary" data-testid="import-import-another">
                  {lang === "fr" ? "Importer un autre fichier" : "Import another file"}
                </button>
                <button onClick={onClose} className="px-3 py-1.5 text-xs rounded bg-[#002FA7] text-white hover:bg-[#002280] font-semibold" data-testid="import-done">
                  {lang === "fr" ? "Terminé" : "Done"}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  disabled={uploading}
                  className="px-3 py-1.5 text-xs rounded border border-border hover:bg-secondary disabled:opacity-50"
                  data-testid="import-cancel"
                >
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button
                  onClick={submit}
                  disabled={uploading || !file}
                  className="px-4 py-1.5 text-xs rounded bg-[#002FA7] text-white hover:bg-[#002280] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="import-submit"
                >
                  {uploading ? <><SpinnerGap size={12} className="animate-spin" /> {lang === "fr" ? "Import…" : "Importing…"}</> : <><UploadSimple size={12} weight="bold" /> {lang === "fr" ? "Importer" : "Import"}</>}
                </button>
              </>
            )}
          </div>

          {/* Help section */}
          <details className="text-[10px] text-muted-foreground">
            <summary className="cursor-pointer hover:text-zinc-700">
              {lang === "fr" ? "📘 Voir un exemple de format CSV" : "📘 See CSV format example"}
            </summary>
            <pre className="mt-1 p-2 bg-secondary rounded text-[9px] overflow-x-auto">
{kind === "qa" && `question,answer,tags
Comment ouvrir un REER?,Visiter une succursale ou en ligne.,REER;Onboarding
Quelle est la différence entre CELI et CELIAPP?,Le CELI est généraliste tandis que le CELIAPP est dédié à l'achat d'une première maison.,CELI`}
{kind === "procedure" && `title,summary,steps
Ouvrir un CELI,Procédure standard.,1) Vérifier l'identité|2) Remplir formulaire|3) Signer
Faire un retrait CELI,Sans frais ni limite.,1) Identifier le compte|2) Vérifier solde`}
{kind === "script" && `title,text
Appel de bienvenue,"Bonjour, je suis votre conseiller..."
Suivi 30 jours,"Bonjour, comment vont vos investissements?"`}
{kind === "objection" && `objection,response
C'est trop cher,Comparons la valeur à long terme...
Je veux y penser,Quels éléments précis voulez-vous valider?`}
{kind === "concept" && `term,definition
ABF,Acquisition de Bloc d'Affaires - reprise de portefeuille.
REER,Régime Enregistré d'Épargne Retraite.`}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}
